import { createContext, useContext, useState, type ReactNode } from 'react'

/**
 * F27: where a register's column widths live.
 *
 * `DataTable` knows how to resize; it does not know who the user is or where
 * their preferences are kept. The app supplies that through this provider — a
 * hook that, given the register's `layoutKey`, returns the saved widths and a
 * setter (the web app's is server-backed, so widths follow the user to another
 * device). With no provider (a unit test, a story) widths live in the table's
 * own state: resizable, simply not remembered.
 */
export type RegisterWidths = Readonly<Record<string, number>>

export interface RegisterWidthsBinding {
  widths: RegisterWidths | undefined
  /** `undefined` = forget them: the register goes back to automatic widths. */
  setWidths: (next: RegisterWidths | undefined) => void
}

export type UseRegisterWidths = (layoutKey: string | undefined) => RegisterWidthsBinding

const RegisterLayoutContext = createContext<UseRegisterWidths | null>(null)

export function RegisterLayoutProvider({ useWidths, children }: { useWidths: UseRegisterWidths; children: ReactNode }) {
  return <RegisterLayoutContext.Provider value={useWidths}>{children}</RegisterLayoutContext.Provider>
}

function useLocalWidths(): RegisterWidthsBinding {
  const [widths, setWidths] = useState<RegisterWidths | undefined>(undefined)
  return { widths, setWidths }
}

/** The provider's hook never changes for the life of the app, so this is a stable hook call. */
export function useRegisterWidths(layoutKey: string | undefined): RegisterWidthsBinding {
  const useBound = useContext(RegisterLayoutContext) ?? useLocalWidths
  return useBound(layoutKey)
}
